import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { Mail, Plug, Send, Trash2, ExternalLink, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
  CONNECTED: "bg-emerald-100 text-emerald-700 border-emerald-300",
  SAVED: "bg-amber-100 text-amber-700 border-amber-300",
  FAILED: "bg-red-100 text-red-700 border-red-300",
  NOT_CONFIGURED: "bg-slate-100 text-slate-500 border-slate-300",
};

export default function EmailConnections() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cfgOpen, setCfgOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ gmail_email: "", gmail_app_password: "", from_name: "" });
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/email-connections");
      setRows(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openConfig = (r) => {
    setActive(r);
    setForm({ gmail_email: r.gmail_email || "", gmail_app_password: "", from_name: r.from_name || r.company_name || "" });
    setCfgOpen(true);
  };

  const saveAndConnect = async () => {
    if (!form.gmail_email.trim()) return toast.error("Gmail address is required");
    if (!form.gmail_app_password.trim() && !active.app_password_set) return toast.error("App Password is required");
    setBusy("connect");
    try {
      await api.put(`/admin/email-connections/${active.tenant_id}`, {
        gmail_email: form.gmail_email.trim(),
        gmail_app_password: form.gmail_app_password.trim() || null,
        from_name: form.from_name.trim() || null,
      });
      await api.post(`/admin/email-connections/${active.tenant_id}/connect`);
      toast.success(`Gmail connected for ${active.company_name} ✅`);
      setCfgOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      load();
    } finally { setBusy(""); }
  };

  const testSend = async (r) => {
    setBusy(`test-${r.tenant_id}`);
    try {
      const { data } = await api.post(`/admin/email-connections/${r.tenant_id}/test`, {});
      toast.success(`Test email sent to ${data.to} via ${r.gmail_email}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(""); }
  };

  const disconnect = async (r) => {
    setBusy(`del-${r.tenant_id}`);
    try {
      await api.delete(`/admin/email-connections/${r.tenant_id}`);
      toast.success(`${r.company_name} disconnected — falls back to platform SMTP`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(""); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="email-connections-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black flex items-center gap-2">
            <Mail className="w-7 h-7 text-orange-500" /> Tenant email connections
          </h1>
          <p className="text-muted-foreground max-w-2xl">
            Connect each approved tenant's own Gmail so their system emails (welcome, approvals, reminders) are sent from their address.
            Tenants without a connection fall back to the platform default SMTP.
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Use a Google <b>App Password</b> — generate one at{" "}
            <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-orange-600 underline">myaccount.google.com/apppasswords</a>{" "}
            (requires 2-Step Verification on the Gmail account).
          </p>
        </div>
        <Button variant="outline" onClick={load} data-testid="email-conn-refresh-btn">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      <Card><CardContent className="p-4 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Company</TableHead>
            <TableHead>Admin user</TableHead>
            <TableHead>Portal URL</TableHead>
            <TableHead>Gmail</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.tenant_id} data-testid={`email-conn-row-${r.tenant_id}`}>
                <TableCell>
                  <div className="font-semibold">{r.company_name}</div>
                  <div className="text-[10px] text-muted-foreground font-mono">{r.slug} · {r.plan_code || "—"}</div>
                </TableCell>
                <TableCell>
                  <div className="text-xs">{r.admin_email || "—"}</div>
                  {r.admin_role && <Badge variant="outline" className="font-mono text-[9px] mt-0.5">{r.admin_role}</Badge>}
                </TableCell>
                <TableCell>
                  <a href={r.portal_url} target="_blank" rel="noreferrer"
                     className="text-xs text-orange-600 hover:underline inline-flex items-center gap-1 font-mono max-w-[220px] truncate"
                     data-testid={`email-conn-url-${r.tenant_id}`}>
                    {r.portal_url.replace(/^https?:\/\//, "")} <ExternalLink className="w-3 h-3 shrink-0" />
                  </a>
                </TableCell>
                <TableCell className="text-xs font-mono">{r.gmail_email || "—"}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={`text-[10px] font-bold ${STATUS_STYLES[r.status] || STATUS_STYLES.NOT_CONFIGURED}`}
                         data-testid={`email-conn-status-${r.tenant_id}`}>
                    {r.status === "CONNECTED" ? "✓ CONNECTED" : r.status.replace("_", " ")}
                  </Badge>
                  {r.status === "FAILED" && r.error && (
                    <div className="text-[10px] text-red-500 mt-1 max-w-[180px] truncate" title={r.error}>{r.error}</div>
                  )}
                  {r.last_verified_at && r.status === "CONNECTED" && (
                    <div className="text-[9px] text-muted-foreground mt-0.5">verified {new Date(r.last_verified_at).toLocaleString()}</div>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-1.5 justify-end">
                    <Button size="sm" variant={r.status === "CONNECTED" ? "outline" : "default"} onClick={() => openConfig(r)}
                            data-testid={`email-conn-configure-${r.tenant_id}`}>
                      <Plug className="w-3 h-3 mr-1" /> {r.status === "NOT_CONFIGURED" ? "Connect" : "Configure"}
                    </Button>
                    {r.status === "CONNECTED" && (
                      <Button size="sm" variant="outline" disabled={busy === `test-${r.tenant_id}`} onClick={() => testSend(r)}
                              data-testid={`email-conn-test-${r.tenant_id}`}>
                        <Send className="w-3 h-3 mr-1" /> {busy === `test-${r.tenant_id}` ? "Sending…" : "Test"}
                      </Button>
                    )}
                    {r.status !== "NOT_CONFIGURED" && (
                      <Button size="sm" variant="ghost" disabled={busy === `del-${r.tenant_id}`} onClick={() => disconnect(r)}
                              data-testid={`email-conn-disconnect-${r.tenant_id}`}>
                        <Trash2 className="w-3 h-3 text-muted-foreground" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">No approved tenants yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Dialog open={cfgOpen} onOpenChange={setCfgOpen}>
        <DialogContent className="max-w-md" data-testid="email-conn-dialog">
          <DialogHeader>
            <DialogTitle>Connect Gmail — {active?.company_name}</DialogTitle>
            <DialogDescription>
              Enter the tenant's Gmail address and Google App Password. Clicking Connect verifies the credentials live against Gmail's servers.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="ec-gmail">Gmail address *</Label>
              <Input id="ec-gmail" type="email" value={form.gmail_email}
                     onChange={(e) => setForm({ ...form, gmail_email: e.target.value })}
                     placeholder="company@gmail.com" data-testid="email-conn-gmail-input" />
            </div>
            <div>
              <Label htmlFor="ec-pass">Google App Password {active?.app_password_set ? "(saved — leave blank to keep)" : "*"}</Label>
              <Input id="ec-pass" type="password" value={form.gmail_app_password}
                     onChange={(e) => setForm({ ...form, gmail_app_password: e.target.value })}
                     placeholder="xxxx xxxx xxxx xxxx" data-testid="email-conn-password-input" />
              <p className="text-[10px] text-muted-foreground mt-1">16-character key from myaccount.google.com/apppasswords — spaces are OK.</p>
            </div>
            <div>
              <Label htmlFor="ec-from">Sender name</Label>
              <Input id="ec-from" value={form.from_name}
                     onChange={(e) => setForm({ ...form, from_name: e.target.value })}
                     placeholder={active?.company_name || "Company name"} data-testid="email-conn-fromname-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCfgOpen(false)} data-testid="email-conn-cancel">Cancel</Button>
            <Button onClick={saveAndConnect} disabled={busy === "connect"} data-testid="email-conn-connect-btn">
              <Plug className="w-4 h-4 mr-1" /> {busy === "connect" ? "Connecting…" : "Save & Connect"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
